import { Link } from "wouter";
import { Shield, Check, Trophy, Instagram } from "lucide-react";
import { siteConfig, whatsappHref } from "@/site.config";
import { track } from "@/lib/tracking";
import { PERSONAS } from "@/data/personas";

const IconWhatsApp = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const CONSORCIOS = [
  { label: "Veículos", href: "/consorcio/veiculos" },
  { label: "Motos", href: "/consorcio/motos" },
  { label: "Imóveis", href: "/consorcio/imoveis" },
  { label: "Agronegócio", href: "/consorcio/agro" },
  { label: "Pesados", href: "/consorcio/pesados" },
  { label: "Serviços", href: "/consorcio/servicos" },
];

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer
      className="w-full bg-[var(--sr-navy-deep)] py-14"
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">
        Rodapé
      </h2>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand + contato direto */}
          <div>
            <p className="sr-display italic text-[var(--sr-gold-light)] text-2xl mb-3">
              {siteConfig.brand.name}
            </p>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Consórcio com atendimento consultivo para veículos, motos,
              imóveis, agro, pesados e serviços. Sem juros e sem entrada.
            </p>
            <a
              href={whatsappHref()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_clicked", { source: "footer" })}
              className="inline-flex items-center gap-2 bg-[var(--sr-green-cta)] text-white text-sm font-semibold px-4 py-2.5 rounded-[var(--radius-sm-token)] hover:brightness-110 transition sr-focus-ring"
            >
              <IconWhatsApp className="w-4 h-4" />
              {siteConfig.brand.whatsapp.displayNumber}
            </a>
            <p className="text-white/60 text-xs mt-4">{siteConfig.brand.hours}</p>
          </div>

          {/* Consórcios */}
          <nav aria-label="Consórcios">
            <h3 className="text-white font-semibold text-sm mb-4">Consórcios</h3>
            <ul className="space-y-2.5">
              {CONSORCIOS.map(l => (
                <li key={l.label}>
                  <Link
                    href={l.href}
                    className="text-white/60 text-sm hover:text-white transition-colors sr-focus-ring rounded"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Consultores + Institucional */}
          <nav aria-label="Consultores e institucional">
            <h3 className="text-white font-semibold text-sm mb-4">
              Fale com um consultor
            </h3>
            <ul className="space-y-2.5 mb-6">
              {PERSONAS.map(p => (
                <li key={p.slug}>
                  <a
                    href={p.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    onClick={() =>
                      track("instagram_clicked", { persona: p.slug })
                    }
                    className="inline-flex items-center gap-2 text-white/60 text-sm hover:text-white transition-colors sr-focus-ring rounded"
                  >
                    <Instagram className="w-3.5 h-3.5" aria-hidden="true" />
                    {p.fullName.split(" ")[0]}
                  </a>
                </li>
              ))}
            </ul>
            <ul className="space-y-2.5">
              <li>
                <Link
                  href="/contato"
                  className="text-white/60 text-sm hover:text-white transition-colors sr-focus-ring rounded"
                >
                  Contato
                </Link>
              </li>
              <li>
                <Link
                  href={siteConfig.legal.privacyPolicyPath}
                  className="text-white/60 text-sm hover:text-white transition-colors sr-focus-ring rounded"
                >
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <Link
                  href={siteConfig.legal.termsPath}
                  className="text-white/60 text-sm hover:text-white transition-colors sr-focus-ring rounded"
                >
                  Termos de Uso
                </Link>
              </li>
            </ul>
          </nav>

          {/* Segurança / compliance */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-4">
              Segurança e regulamentação
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <Shield
                  className="w-4 h-4 text-[var(--sr-gold)] shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <a
                  href={siteConfig.legal.bacenLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 text-sm hover:text-white transition-colors sr-focus-ring rounded"
                >
                  Fiscalizado pelo Banco Central
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Check
                  className="w-4 h-4 text-[var(--sr-gold)] shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <a
                  href={siteConfig.legal.consorcioLawLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 text-sm hover:text-white transition-colors sr-focus-ring rounded"
                >
                  Lei nº 11.795/2008
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Trophy
                  className="w-4 h-4 text-[var(--sr-gold)] shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span className="text-white/60 text-sm">
                  Administradoras autorizadas Bacen
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-white/60 text-xs">
            © {year} {siteConfig.brand.name}. Todos os direitos reservados.
          </span>
          <p className="text-white/60 text-[11px] text-center sm:text-right max-w-lg leading-relaxed">
            Consórcios administrados por administradoras autorizadas e
            fiscalizadas pelo Banco Central do Brasil (Lei 11.795/2008). Valores
            de simulação são ilustrativos e sujeitos a aprovação cadastral.
            Consórcio Bazilio atua como canal comercial.
          </p>
        </div>
      </div>
    </footer>
  );
}
